import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import styles from './CommandPalette.module.css';

interface Command {
  id: string;
  label: string;
  description?: string;
  icon: string;
  action: () => void;
  altAction?: () => void;
  altLabel?: string;
  keywords?: string[];
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

function useCommands(onClose: () => void): Command[] {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isEmployer = user?.roles.includes('employer');
  const isEmployee = user?.roles.includes('employee');
  const isAdmin = user?.roles.includes('admin');

  const go = useCallback(
    (path: string) => {
      navigate(path);
      onClose();
    },
    [navigate, onClose]
  );

  const commands: Command[] = [];

  if (isEmployer) {
    commands.push(
      {
        id: 'employer-dashboard',
        label: t('nav.dashboard'),
        icon: 'gauge',
        action: () => go('/dashboard'),
        keywords: ['home'],
      },
      {
        id: 'employees',
        label: t('nav.employees'),
        icon: 'users',
        action: () => go('/employees'),
        altAction: () => go('/employees/new'),
        altLabel: t('cmd.addEmployee'),
        keywords: ['add', 'new', 'create'],
      },
      {
        id: 'shifts',
        label: t('nav.shifts'),
        icon: 'clock',
        action: () => go('/shifts'),
        altAction: () => go('/shifts?modal=create'),
        altLabel: t('cmd.addShift'),
        keywords: ['work', 'time'],
      },
      {
        id: 'payslips',
        label: t('nav.salary'),
        icon: 'file-invoice-dollar',
        action: () => go('/salary'),
        altAction: () => go('/salary?modal=generate'),
        altLabel: t('cmd.generatePayslip'),
      },
      {
        id: 'reports',
        label: t('nav.reports'),
        icon: 'chart-bar',
        action: () => go('/reports'),
      },
      {
        id: 'taxes',
        label: t('nav.taxes'),
        icon: 'building-columns',
        action: () => go('/taxes'),
      },
    );
  }

  if (isEmployee) {
    commands.push(
      {
        id: 'employee-dashboard',
        label: t('nav.myDashboard'),
        icon: 'gauge',
        action: () => go('/my/dashboard'),
        keywords: ['home'],
      },
      {
        id: 'my-employers',
        label: t('nav.myEmployers'),
        icon: 'building',
        action: () => go('/my/employers'),
      },
      {
        id: 'my-shifts',
        label: t('nav.myShifts'),
        icon: 'clock',
        action: () => go('/my/shifts'),
        altAction: () => go('/my/shifts?modal=create'),
        altLabel: t('cmd.logShift'),
        keywords: ['work', 'time', 'log'],
      },
      {
        id: 'my-pay',
        label: t('nav.myPay'),
        icon: 'file-invoice-dollar',
        action: () => go('/my/pay'),
      },
      {
        id: 'my-taxes',
        label: t('nav.myTaxes'),
        icon: 'percent',
        action: () => go('/my/taxes'),
      },
    );
  }

  if (isAdmin) {
    commands.push({
      id: 'admin',
      label: t('cmd.adminDashboard'),
      icon: 'shield-halved',
      action: () => go('/admin'),
    });
  }

  return commands;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [altHeld, setAltHeld] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const commands = useCommands(onClose);

  const filtered = query.trim()
    ? commands.filter((c) => {
        const q = query.toLowerCase();
        return (
          c.label.toLowerCase().includes(q) ||
          c.description?.toLowerCase().includes(q) ||
          c.keywords?.some((k) => k.toLowerCase().includes(q))
        );
      })
    : commands;

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setAltHeld(false);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Track Alt key globally while palette is open.
  // preventDefault on Alt keydown prevents browsers (especially Windows) from
  // moving focus to the browser menu bar when Alt is pressed alone.
  useEffect(() => {
    if (!open) return;
    function onDown(e: KeyboardEvent) {
      if (e.key === 'Alt') {
        e.preventDefault();
        setAltHeld(true);
      }
    }
    function onUp(e: KeyboardEvent) {
      if (e.key === 'Alt') setAltHeld(false);
    }
    // Reset altHeld if the window itself loses focus (e.g. user Alt+Tabs away)
    function onWindowBlur() {
      setAltHeld(false);
    }
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [open]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[activeIndex];
      if (!cmd) return;
      if (e.altKey && cmd.altAction) {
        cmd.altAction();
      } else {
        cmd.action();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  if (!open) return null;

  const activeCmd = filtered[activeIndex];

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.palette}
        onClick={(e) => e.stopPropagation()}
        onBlur={(e) => {
          // If focus leaves the palette entirely, pull it back to the input
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
        role="dialog"
        aria-label={t('cmd.ariaLabel')}
      >
        <div className={styles.searchRow}>
          <i className={`fa-solid fa-magnifying-glass ${styles.searchIcon}`} aria-hidden="true" />
          <input
            ref={inputRef}
            className={styles.input}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('cmd.placeholder')}
            aria-label={t('cmd.placeholder')}
          />
          <kbd className={styles.escKey}>ESC</kbd>
        </div>

        {filtered.length === 0 ? (
          <div className={styles.empty}>{t('cmd.noResults')}</div>
        ) : (
          <>
            <ul className={styles.list} role="listbox">
              {filtered.map((cmd, i) => (
                <li
                  key={cmd.id}
                  role="option"
                  aria-selected={i === activeIndex}
                  className={[styles.item, i === activeIndex ? styles.active : ''].join(' ')}
                  onClick={() => (altHeld && cmd.altAction ? cmd.altAction() : cmd.action())}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  <i className={`fa-solid fa-${cmd.icon} ${styles.itemIcon}`} aria-hidden="true" />
                  <div className={styles.itemText}>
                    <span className={styles.itemLabel}>{cmd.label}</span>
                    {cmd.description && (
                      <span className={styles.itemDesc}>{cmd.description}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {activeCmd && (
              <div className={styles.footer}>
                <span className={styles.footerHint}>
                  <kbd className={styles.kbd}>↵</kbd>
                  {t('cmd.hintOpen')}
                </span>
                {activeCmd.altAction && activeCmd.altLabel && (
                  <span className={`${styles.footerHint} ${altHeld ? styles.footerHintActive : ''}`}>
                    <kbd className={styles.kbd}>⌥↵</kbd>
                    {activeCmd.altLabel}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
